/**
 * About View
 *
 * Displays game information using DopeUI for consistent styling.
 */

import { registerView } from '../viewRegistry';
import { getCanonicalSafeArea } from '../safeArea';
import { interactiveRegistry, navigateTo } from '../interactivity';
import DopeUI, { TERMINAL, SPACING } from '../ui/DopeOS-UI';

const ABOUT_PARAGRAPHS = [
  'Dope Chain Wars is a work of interactive fiction set in a fictionalized 1980s New York and the Orbis Tertius archive.',
  'You play a courier and trader moving Bio-Feed, Twitch-Stim, and the occasional Hronir through a city that audits everything.',
  'This is a software toy: a narrative trading experiment. It is not a financial product, investment advice, or a guide to real-world crime.',
  'Version 0 runs off-chain with soft in-game dollars only. No external assets, real-money purchases, cash-out, or financial rewards are required or offered.',
  'Vinnie lends. The Auditor scans. The Hronir asks you to liquidate, archive, or expose it. Every choice has a cost.',
  'A run is 30 playable days. Day 31 is the Purge: debts settle, audits close, and the archive keeps an incomplete record of what you did.',
  '18+ fiction. Play carefully, outsmart the scan, and leave a signature worth archiving.'
];

const aboutState = {
  scrollOffset: 0,
  maxScrollPx: 0,
  isTouching: false,
  touchStartY: 0,
};

function wrapText(text, maxPx) {
  const maxChars = Math.max(20, Math.floor(maxPx / 8));
  const words = text.split(' ');
  const lines = [];
  let current = '';

  words.forEach((word) => {
    const test = current ? `${current} ${word}` : word;
    if (test.length > maxChars && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  });

  if (current) lines.push(current);
  return lines;
}

function renderAbout(ctx, time, w, h, params = {}) {
  // Clear interactions at START
  interactiveRegistry.clear();

  // Black background
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, w, h);

  const safe = getCanonicalSafeArea({ w, h });
  const scale = 1;
  const lineHeight = 18;

  // Header panel with title
  DopeUI.Panel(ctx, safe.left, safe.top, safe.width, 40, 'ABOUT');

  // Content area (inside panel)
  const contentX = safe.left + SPACING.MD;
  const contentY = safe.top + 50;
  const contentW = safe.width - SPACING.MD * 2;
  const contentH = safe.height - 100;

  // Build wrapped lines
  const allLines = [];
  ABOUT_PARAGRAPHS.forEach((p, idx) => {
    const wrapped = wrapText(p, contentW);
    allLines.push(...wrapped);
    if (idx < ABOUT_PARAGRAPHS.length - 1) {
      allLines.push('', ''); // Paragraph spacing
    }
  });

  const totalHeight = allLines.length * lineHeight;
  aboutState.maxScrollPx = Math.max(0, totalHeight - contentH);

  // Clamp scroll
  aboutState.scrollOffset = Math.max(0, Math.min(aboutState.scrollOffset, aboutState.maxScrollPx));

  // Main content panel
  DopeUI.Panel(ctx, contentX - SPACING.SM, contentY - SPACING.SM, contentW + SPACING.MD, contentH + SPACING.MD);

  // Clip content
  ctx.save();
  ctx.beginPath();
  ctx.rect(contentX, contentY, contentW, contentH);
  ctx.clip();

  // Render text
  let y = contentY - aboutState.scrollOffset;

  allLines.forEach(line => {
    if (y + lineHeight >= contentY && y <= contentY + contentH) {
      if (line) {
        DopeUI.Text(ctx, line, contentX, y, 'normal', scale);
      }
    }
    y += lineHeight;
  });

  ctx.restore();

  // Scrollbar using DopeUI
  if (aboutState.maxScrollPx > 0) {
    const scrollProgress = aboutState.scrollOffset / aboutState.maxScrollPx;
    const thumbSize = Math.max(0.1, contentH / totalHeight);

    DopeUI.Scrollbar(
      ctx,
      safe.right - 15,
      contentY,
      10,
      contentH,
      scrollProgress,
      thumbSize
    );
  }

  // Close button using DopeUI
  const closeBtnX = safe.right - 80;
  const closeBtnY = safe.top + 5;
  DopeUI.Button(ctx, closeBtnX, closeBtnY, 70, 28, 'CLOSE', 'normal');

  // Register close interaction
  interactiveRegistry.register({
    id: 'about-close',
    x: closeBtnX,
    y: closeBtnY,
    w: 70,
    h: 28,
    onClick: () => {
      navigateTo('main_menu');
      aboutState.scrollOffset = 0;
    }
  });
}

// Wheel handler for scrolling
if (typeof window !== 'undefined') {
  window.addEventListener('wheel', (e) => {
    if (window.DCW_CURRENT_VIEW === 'about') {
      aboutState.scrollOffset += e.deltaY * 0.5;
      aboutState.scrollOffset = Math.max(0, Math.min(aboutState.scrollOffset, aboutState.maxScrollPx));
      e.preventDefault();
    }
  }, { passive: false });
}

// Register view immediately
registerView('about', renderAbout);

export default { viewName: 'about' };
